import { alsoAsAvailability, type AlsoAs, type AlsoAsMediaKind } from '@mpr/shared';

/**
 * The composer's post types and the "also publish as" choices, kept apart from
 * the form so each rule can be tested without rendering anything.
 */
export const COMPOSER_KINDS = [
  { id: 'TEXT', media: 0 },
  { id: 'LINK', media: 0 },
  { id: 'PHOTO', media: 1 },
  { id: 'MULTI_PHOTO', media: 10 },
  { id: 'VIDEO', media: 1 },
  { id: 'REEL', media: 1 },
  { id: 'PHOTO_STORY', media: 1 },
  { id: 'VIDEO_STORY', media: 1 },
] as const;

export type ComposerKindId = (typeof COMPOSER_KINDS)[number]['id'];

/** Kinds whose single file is a video. */
export function isVideoKind(kind: string): boolean {
  return kind === 'VIDEO' || kind === 'VIDEO_STORY' || kind === 'REEL';
}

export interface AlsoAsChoice {
  /** Shown at all: only kinds that carry media. */
  visible: boolean;
  story: boolean;
  reel: boolean;
}

/**
 * Which of the two checkboxes are usable.
 *
 * Story: exactly one photo or video, and no schedule (the Stories API cannot be
 * scheduled). Reel: exactly one video. Same rule the server applies.
 */
export function alsoAsChoices(kind: string, attachedCount: number, scheduled: boolean): AlsoAsChoice {
  const carriesMedia = COMPOSER_KINDS.some((entry) => entry.id === kind && entry.media > 0);
  const media: AlsoAsMediaKind[] = Array.from({ length: attachedCount }, () =>
    isVideoKind(kind) ? 'video' : 'image',
  );
  const available = alsoAsAvailability(kind, media);
  return {
    visible: carriesMedia,
    story: available.story && !scheduled,
    reel: available.reel,
  };
}

/** The ticked boxes that are still usable, as the request's `alsoAs`. */
export function pickAlsoAs(
  ticked: { story: boolean; reel: boolean },
  choice: AlsoAsChoice,
): AlsoAs[] {
  const out: AlsoAs[] = [];
  if (ticked.story && choice.story) out.push('STORY');
  if (ticked.reel && choice.reel) out.push('REEL');
  return out;
}
